/**
 * Medical file viewer.
 *
 * Ported from `viewMedicalFile` in shared_ui.js. The reason it converts a data
 * URI to a Blob before displaying is unchanged and still necessary: Chrome
 * blocks navigating to a `data:` URI, so images and PDFs have to be handed over
 * as object URLs.
 *
 * The legacy version leaked those object URLs whenever the modal was closed by
 * any route other than its own close button; this one revokes on unmount.
 */

import { useEffect, useMemo, useState } from 'react';
import { Icon, Modal, Button } from './index';
import { dataUriToBlob, isDataUri, parseDataUri, canRenderInline, formatBytes, dataUriByteLength } from '../../domain/files';

export function MedicalFileViewer({ open, onClose, file }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [error, setError] = useState(null);

  const source = file?.data ?? file?.resultUrl ?? null;
  const filename = file?.name || 'نتيجة طبية';

  const mimeType = useMemo(() => {
    if (!isDataUri(source)) return null;
    return parseDataUri(source)?.mimeType || null;
  }, [source]);

  useEffect(() => {
    if (!open || !source) {
      setObjectUrl(null);
      setError(null);
      return undefined;
    }

    // A plain URL needs no conversion.
    if (!isDataUri(source)) {
      setObjectUrl(source);
      return undefined;
    }

    let url = null;
    try {
      const blob = dataUriToBlob(source);
      if (!blob) throw new Error('صيغة الملف غير صالحة');
      url = URL.createObjectURL(blob);
      setObjectUrl(url);
      setError(null);
    } catch (err) {
      console.error('[FileViewer] Could not decode the attachment:', err.message);
      setError('تعذر فتح الملف — قد تكون البيانات تالفة.');
    }

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, source]);

  const size = useMemo(() => (isDataUri(source) ? dataUriByteLength(source) : 0), [source]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={filename}
      icon="file-text"
      size="xl"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-slate-400">
            {mimeType || 'ملف'} {size > 0 && `• ${formatBytes(size)}`}
          </span>
          {objectUrl && (
            <a
              href={objectUrl}
              download={filename}
              className="btn-primary"
            >
              <Icon name="download" size={16} />
              تحميل
            </a>
          )}
        </div>
      }
    >
      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-center text-sm text-red-300">
          {error}
        </div>
      )}

      {!error && objectUrl && canRenderInline(mimeType) && (
        mimeType === 'application/pdf' ? (
          <iframe
            src={objectUrl}
            title={filename}
            className="h-[70vh] w-full rounded-lg border-0 bg-white"
          />
        ) : (
          <img
            src={objectUrl}
            alt={filename}
            className="mx-auto max-h-[70vh] rounded-lg object-contain shadow-2xl"
          />
        )
      )}

      {!error && objectUrl && !canRenderInline(mimeType) && (
        <div className="py-10 text-center text-sm text-slate-300">
          <Icon name="file" size={40} className="mx-auto mb-3 text-slate-500" />
          لا يمكن معاينة هذا النوع من الملفات مباشرة — استخدم زر التحميل بالأسفل.
        </div>
      )}

      {!error && !objectUrl && (
        <div className="py-10 text-center text-sm text-slate-400">لا يوجد ملف لعرضه.</div>
      )}
    </Modal>
  );
}

/**
 * A row of attachment chips for one lab test or x-ray, with the patient's upload
 * action or the doctor's comment action. Replaces `renderResultsGallery`, which
 * returned an HTML string with `onclick` handlers embedded in it.
 */
export function ResultsGallery({
  files = [],
  doctorComment = null,
  isDoctor = false,
  onUpload,
  onComment,
  onView,
}) {
  return (
    <div className="mt-2 space-y-2">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file, index) => (
            <button
              key={`${file.name || 'file'}-${index}`}
              type="button"
              onClick={() => onView?.(file)}
              className="flex items-center gap-1.5 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1.5 text-[10px] text-emerald-300 transition-colors hover:bg-emerald-500/20"
            >
              <Icon name="file-check-2" size={14} />
              {file.name || `مرفق ${index + 1}`}
            </button>
          ))}
        </div>
      )}

      {doctorComment && (
        <div className="flex items-start gap-2 rounded-lg border-r-2 border-indigo-500 bg-indigo-500/10 p-2 text-xs text-indigo-200">
          <Icon name="message-square-text" size={14} className="mt-0.5 shrink-0 text-indigo-400" />
          <div className="leading-relaxed">
            <strong>تعليق الطبيب:</strong> {doctorComment}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        {isDoctor ? (
          <Button variant="subtle" icon="edit-3" onClick={onComment} className="!py-1.5 !text-[10px]">
            إضافة/تعديل تعليق
          </Button>
        ) : (
          <Button variant="subtle" icon="upload-cloud" onClick={onUpload} className="!py-1.5 !text-[10px]">
            {files.length > 0 ? 'إضافة ملفات أخرى' : 'رفع النتيجة'}
          </Button>
        )}
      </div>
    </div>
  );
}
